import React, { useState, useEffect, useRef, useMemo, lazy, Suspense, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { 
  Search, Share2, Target, Monitor, Video, Bot, Headphones, Sparkles, MessageSquare,
  ArrowRight, Mail, Phone, MapPin, Menu, X, CheckCircle, BarChart3,
  ShoppingBag, Laptop, Heart, Briefcase, Home as HomeIcon, TrendingUp,
  Zap, Clock, Award, Users, Play, ChevronDown, Star, ArrowUpRight,
  Lightbulb, Rocket, Shield, Globe, PieChart, LineChart
} from 'lucide-react';
import { services, testimonials, industries } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import FluidBackground from '../components/FluidBackground';
import Card3D from '../components/Card3D';
import ScrollFlow3D from '../components/ScrollFlow3D';
import ServicesScrollShowcase from '../components/ServicesScrollShowcase';
import RansenAcronym from '../components/RansenAcronym';
import Header from '../components/Header';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Hook to detect mobile for performance optimization
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(true); // Default to true for SSR/mobile-first
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

const iconMap = {
  Search, Share2, Target, Monitor, Video, Bot, Headphones,
  ShoppingBag, Laptop, Heart, Briefcase, Home: HomeIcon, TrendingUp,
  Sparkles, MessageSquare
};

// Animated counter component - Optimized with RAF
const AnimatedCounter = memo(({ value, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isInView && !hasAnimated.current) {
      hasAnimated.current = true;
      const end = parseInt(value);
      const startTime = performance.now();
      const totalDuration = duration * 1000;

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeOutQuart * end));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
});

// Floating 3D element component - Simplified for performance
const Floating3DElement = memo(({ children, delay = 0, duration = 6, isMobile = false }) => {
  if (isMobile) {
    return <div>{children}</div>;
  }
  
  return (
    <motion.div
      animate={{
        y: [0, -12, 0],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );
});

// Parallax disabled for performance - just render children
const ParallaxLayer = memo(({ children, className = '' }) => {
  return (
    <div className={className}>
      {children}
    </div>
  );
});

// Glowing orb background component - CSS only for performance
const GlowingOrbs = memo(({ isMobile = false }) => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div 
        className="absolute top-1/4 left-1/4 w-64 h-64 md:w-96 md:h-96 bg-brand/15 rounded-full"
        style={{ filter: 'blur(80px)' }}
      />
      {!isMobile && (
        <>
          <div 
            className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent-pink/8 rounded-full"
            style={{ filter: 'blur(80px)' }}
          />
          <div 
            className="absolute top-1/2 right-1/3 w-64 h-64 bg-accent-cyan/8 rounded-full"
            style={{ filter: 'blur(80px)' }}
          />
        </>
      )}
    </div>
  );
});

const Home = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', website: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const heroRef = useRef(null);
  const isMobile = useIsMobile();
  
  // Always call hooks unconditionally
  const { scrollYProgress } = useScroll();
  const heroOpacityTransform = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScaleTransform = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);
  
  // Use static values on mobile for performance
  const heroOpacity = isMobile ? 1 : heroOpacityTransform;
  const heroScale = isMobile ? 1 : heroScaleTransform;

  // Simplified animation variants
  const fadeInUp = useMemo(() => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4 }
  }), []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        scrollToSection(location.state.scrollTo);
      }, 100);
    }
  }, [location]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          website: formData.website || null,
          message: formData.message,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Message Sent!", {
          description: data.message || "Thank you for reaching out. We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', website: '', message: '' });
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error("Error", {
        description: "There was an issue sending your message. Please try again or email us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqItems = [
    {
      question: "What industries do you specialize in?",
      answer: "We specialize in 12+ industries including E-Commerce, SaaS, Healthcare, Real Estate, Finance, Legal, and more. Our team has deep expertise in creating targeted marketing strategies for each sector."
    },
    {
      question: "How long does it take to see results from digital marketing?",
      answer: "Results vary by strategy. PPC campaigns can show results within days, while SEO typically takes 3-6 months for significant improvements. We provide monthly reports to track progress and ROI."
    },
    {
      question: "What makes RANSEN different from other agencies?",
      answer: "We combine AI-powered automation with human creativity. Our data-driven approach, transparent reporting, and focus on LLMO and AEO optimization keeps you ahead of the curve."
    },
    {
      question: "Do you offer custom marketing packages?",
      answer: "Yes! Every business is unique. We create tailored strategies based on your goals, budget, and industry. Schedule a free consultation to discuss your specific needs."
    },
    {
      question: "What is LLMO and AEO optimization?",
      answer: "LLMO (Large Language Model Optimization) ensures your brand appears in AI-generated responses. AEO (Answer Engine Optimization) targets featured snippets and voice search results."
    }
  ];

  const processSteps = [
    { icon: Lightbulb, title: "Discovery", desc: "We analyze your business, competitors, and target audience to identify growth opportunities." },
    { icon: Target, title: "Strategy", desc: "Custom marketing plans designed to maximize ROI and achieve your specific business goals." },
    { icon: Rocket, title: "Execute", desc: "Our team implements campaigns across all channels with precision and creativity." },
    { icon: LineChart, title: "Optimize", desc: "Continuous monitoring and data-driven optimization to improve performance." }
  ];

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-void text-white overflow-x-clip">
      <SEO 
        title="Digital Marketing Agency Canada | SEO, PPC, AI Marketing"
        description="RANSEN is Canada's leading AI-driven digital marketing agency. We deliver Paid Media, SEO, Social Media Marketing, Video Production, AI Automation, LLMO & AEO services. 500+ projects with 98% client satisfaction."
        keywords="digital marketing agency Canada, SEO company Canada, PPC management, social media marketing agency, video production, AI marketing automation, LLMO optimization, AEO optimization, Google Ads agency, Meta Ads management"
        canonical="/"
        schema={homeSchema}
      />
      
      {/* Interactive Fluid Background */}
      <FluidBackground />
      
      {/* Navigation */}
      <Header />

      {/* Hero Section with 3D Elements */}
      <section id="home" ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* 3D Floating elements - hidden on mobile for performance */}
        {!isMobile && (
          <div className="absolute inset-0 pointer-events-none" style={{ perspective: '1000px' }}>
            {/* Floating 3D shapes */}
            <Floating3DElement delay={0} duration={8}>
              <div className="absolute top-1/4 left-[10%] w-32 h-32 bg-gradient-to-br from-brand/30 to-transparent rounded-3xl border border-brand/20 backdrop-blur-sm" 
                   style={{ transform: 'rotateX(20deg) rotateY(-20deg)' }} />
            </Floating3DElement>
            
            <Floating3DElement delay={1} duration={7}>
              <div className="absolute top-1/3 right-[15%] w-24 h-24 bg-gradient-to-br from-accent-pink/20 to-transparent rounded-2xl border border-accent-pink/20 backdrop-blur-sm"
                   style={{ transform: 'rotateX(-15deg) rotateY(25deg)' }} />
            </Floating3DElement>
            
            <Floating3DElement delay={2} duration={9}>
              <div className="absolute bottom-1/3 left-[20%] w-20 h-20 bg-gradient-to-br from-accent-cyan/20 to-transparent rounded-xl border border-accent-cyan/20 backdrop-blur-sm"
                   style={{ transform: 'rotateX(10deg) rotateY(15deg)' }} />
            </Floating3DElement>
            
            <Floating3DElement delay={0.5} duration={6}>
              <div className="absolute bottom-1/4 right-[10%] w-28 h-28 bg-gradient-to-br from-brand/20 to-accent-pink/10 rounded-2xl border border-brand/10 backdrop-blur-sm"
                   style={{ transform: 'rotateX(-20deg) rotateY(-15deg)' }} />
            </Floating3DElement>
            
            {/* Floating icons */}
            <Floating3DElement delay={1.5} duration={10}>
              <div className="absolute top-[20%] right-[25%] w-16 h-16 bg-surface/50 backdrop-blur-xl rounded-2xl border border-brand/20 flex items-center justify-center">
                <TrendingUp className="text-brand" size={28} />
              </div>
            </Floating3DElement>
            
            <Floating3DElement delay={2.5} duration={8}>
              <div className="absolute bottom-[30%] left-[15%] w-14 h-14 bg-surface/50 backdrop-blur-xl rounded-xl border border-accent-cyan/20 flex items-center justify-center">
                <Rocket className="text-accent-cyan" size={24} />
              </div>
            </Floating3DElement>
            
            <Floating3DElement delay={3} duration={7}>
              <div className="absolute top-[40%] left-[8%] w-12 h-12 bg-surface/50 backdrop-blur-xl rounded-lg border border-accent-pink/20 flex items-center justify-center">
                <Zap className="text-accent-pink" size={20} />
              </div>
            </Floating3DElement>
          </div>
        )}

        <motion.div 
          style={{ opacity: heroOpacity, scale: heroScale }}
          className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-6"
          >
            <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand">
              AI-Powered Digital Marketing Agency
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight"
          >
            <span className="bg-gradient-to-r from-brand via-accent-pink to-accent-cyan bg-clip-text text-transparent">
              Marketing Built
            </span>
            <br />
            for the AI Era
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-white/80 mb-12 max-w-4xl mx-auto font-body leading-relaxed"
          >
            Helping businesses grow through paid media, SEO, content marketing, automation, and performance optimization—driving qualified leads and measurable revenue across real estate, retail, professional services, and eCommerce.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16 sm:mb-24"
          >
            <Button 
              onClick={() => scrollToSection('contact')} 
              className="bg-gradient-to-r from-brand to-accent-pink hover:from-brand-dark hover:to-brand text-white rounded-full px-8 py-6 text-lg font-semibold group shadow-lg shadow-brand/30"
            >
              Book Free Strategy Call
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <Button 
              onClick={() => scrollToSection('services')} 
              className="bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-full px-8 py-6 text-lg font-semibold backdrop-blur-sm"
            >
              Explore Services
            </Button>
          </motion.div>

          {/* Scroll indicator - hidden on mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex flex-col items-center text-white/50 bg-void/50 backdrop-blur-sm px-4 py-2 rounded-full"
            >
              <span className="text-sm font-accent mb-1">Scroll to explore</span>
              <ChevronDown size={20} />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface relative">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: 500, suffix: '+', label: 'Projects Delivered', icon: Rocket },
              { value: 98, suffix: '%', label: 'Client Satisfaction', icon: Star },
              { value: 150, suffix: '+', label: 'Happy Clients', icon: Users },
              { value: 320, suffix: '%', label: 'Average ROI', icon: TrendingUp },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-brand/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="text-brand" size={28} />
                </div>
                <div className="text-4xl md:text-5xl font-heading font-bold text-white mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-white/60 font-body">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section — Scroll-Driven Cinematic Showcase */}
      <section id="services">
        <ServicesScrollShowcase />
      </section>

      {/* Why We're Different - AI Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-surface relative overflow-hidden">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block px-4 py-2 bg-accent-cyan/20 border border-accent-cyan/30 rounded-full text-sm font-accent uppercase tracking-wider text-accent-cyan mb-6">
              The RANSEN Difference
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
              Why AI-First Marketing
              <br />
              <span className="bg-gradient-to-r from-brand via-accent-pink to-accent-cyan bg-clip-text text-transparent">
                Changes Everything
              </span>
            </h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto font-body">
              In a world where every business claims to use AI, we're the agency that actually delivers on the promise. 
              Here's what makes us fundamentally different.
            </p>
          </motion.div>

          {/* AI Differentiators Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 bg-void/50 backdrop-blur-sm border border-brand/10 hover:border-brand/30 transition-all h-full">
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-2xl flex items-center justify-center mr-4 border border-red-500/30">
                    <X className="text-red-400" size={28} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-2">Traditional Agencies</h3>
                    <p className="text-white/50 text-sm">The old way of doing things</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {[
                    'Manual campaign management with delayed optimizations',
                    'Generic strategies applied to every client',
                    'Monthly reports that look backwards, not forwards',
                    'Limited to human working hours and capacity',
                    'No understanding of AI search (LLMO/AEO)',
                    'Reactive approach to market changes'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start text-white/60">
                      <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <X className="text-red-400" size={12} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 bg-gradient-to-br from-brand/10 to-accent-cyan/5 backdrop-blur-sm border border-brand/30 hover:border-brand/50 transition-all h-full">
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand to-accent-cyan rounded-2xl flex items-center justify-center mr-4 shadow-lg shadow-brand/30">
                    <Sparkles className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-2">RANSEN AI-First</h3>
                    <p className="text-brand text-sm">The future of marketing</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {[
                    'AI-powered real-time optimization 24/7/365',
                    'Custom strategies built on your unique data',
                    'Predictive analytics that anticipate trends',
                    'Unlimited scalability with AI automation',
                    'Pioneers in LLMO & AEO optimization',
                    'Proactive adaptation to algorithm changes'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start text-white/80">
                      <div className="w-5 h-5 rounded-full bg-brand flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <CheckCircle className="text-white" size={12} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          </div>

          {/* Key AI Features */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Bot,
                title: 'LLMO Optimization',
                desc: 'Get your brand mentioned in ChatGPT, Claude, and other AI assistants. We optimize for the future of search.',
                color: 'brand'
              },
              {
                icon: MessageSquare,
                title: 'Answer Engine Optimization',
                desc: 'Dominate featured snippets and voice search results. Be the answer, not just a result.',
                color: 'accent-pink'
              },
              {
                icon: Zap,
                title: 'AI-Powered Automation',
                desc: 'Our proprietary AI systems work around the clock, optimizing campaigns in real-time for maximum ROI.',
                color: 'accent-cyan'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card className="p-6 bg-void/50 backdrop-blur-sm border border-brand/10 hover:border-brand/30 transition-all h-full text-center">
                  <div className={`w-14 h-14 bg-${feature.color}/20 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <feature.icon className={`text-${feature.color}`} size={28} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="text-white/70 mb-6 text-lg">
              Ready to experience the difference AI-first marketing can make?
            </p>
            <Button 
              onClick={() => scrollToSection('contact')} 
              className="bg-gradient-to-r from-brand-dark to-brand hover:opacity-90 text-white rounded-full px-8 py-6 text-lg font-medium group"
            >
              See What AI Can Do For You
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Scroll-Driven 3D Growth Engine Section */}
      <ScrollFlow3D />

      {/* Process Section */}
      <section id="process" className="py-32 px-4 sm:px-6 lg:px-8 bg-void relative">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand mb-6">
              Our Marketing Process
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
              How We Drive Results
            </h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto font-body">
              Our proven digital marketing strategy combines <a href="/services/search-engine-optimization" className="text-brand hover:underline">SEO</a>, <a href="/services/paid-media-advertising" className="text-brand hover:underline">paid advertising</a>, and conversion optimization to deliver consistent ROI for businesses of all sizes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative"
              >
                <Card className="h-full bg-void/50 backdrop-blur-sm border border-brand/10 hover:border-brand/30 transition-all p-8 text-center">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-brand rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="w-16 h-16 bg-brand/20 rounded-2xl flex items-center justify-center mx-auto mb-6 mt-4">
                    <step.icon className="text-brand" size={32} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-white/60 font-body">{step.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-4 sm:px-6 lg:px-8 bg-void relative">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand mb-6">
                About Our Agency
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Canada's <a href="/ai-digital-marketing-agency" className="text-brand hover:underline">AI-Powered Marketing Agency</a>
              </h2>
              <p className="text-xl text-white/70 mb-6 font-body leading-relaxed">
                RANSEN is a full-service <strong className="text-white"><a href="/ai-digital-marketing-agency" className="text-brand hover:underline">AI-driven digital marketing agency</a></strong> helping businesses across Canada and globally grow through strategic <a href="/services/search-engine-optimization" className="text-brand hover:underline">SEO</a>, <a href="/services/paid-media-advertising" className="text-brand hover:underline">paid advertising</a>, <a href="/services/social-media-management" className="text-brand hover:underline">social media marketing</a>, and <a href="/services/ai-automation" className="text-brand hover:underline">AI automation</a>.
              </p>
              <p className="text-lg text-white/60 mb-8 font-body leading-relaxed">
                Our team of marketing specialists delivers comprehensive solutions including <a href="/services/website-design-development" className="text-brand hover:underline">web design</a>, <a href="/services/video-production" className="text-brand hover:underline">video production</a>, <a href="/services/llmo-optimization" className="text-brand hover:underline">LLMO</a>, <a href="/services/aeo-optimization" className="text-brand hover:underline">AEO</a>, and <a href="/services/business-operations-support" className="text-brand hover:underline">business operations support</a>. We've helped 150+ businesses achieve their growth goals.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                {[
                  { icon: Shield, label: 'Data-Driven' },
                  { icon: Zap, label: 'AI-Powered' },
                  { icon: Globe, label: 'Global Reach' },
                  { icon: Award, label: 'Award Winning' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand/20 rounded-lg flex items-center justify-center">
                      <item.icon className="text-brand" size={20} />
                    </div>
                    <span className="text-white font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => navigate('/ai-digital-marketing-agency')}
                className="bg-brand/20 hover:bg-brand/30 text-brand border border-brand/30 rounded-full px-6 py-3 font-medium group"
              >
                Learn About Our AI Approach
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl overflow-hidden border border-brand/20">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-surface border border-brand/20 rounded-2xl p-6">
                <div className="text-4xl font-heading font-bold text-brand">10+</div>
                <div className="text-white/70">Years Experience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-32 px-4 sm:px-6 lg:px-8 bg-surface relative">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand mb-6">
              Client Success
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card className="h-full bg-void/50 backdrop-blur-sm border border-brand/10 hover:border-brand/30 transition-all p-8">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={18} />
                    ))}
                  </div>
                  <p className="text-white/80 font-body leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-brand/30"
                      loading="lazy"
                    />
                    <div>
                      <h4 className="font-heading font-bold text-white">{testimonial.name}</h4>
                      <p className="text-sm text-white/60">{testimonial.position}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Marketing CTA Section - Salesy */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-surface via-void to-surface relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand/10 rounded-full blur-[120px]" />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-brand/30 border border-brand/50 rounded-full text-sm font-accent uppercase tracking-wider text-white mb-6">
              Stop Wasting Ad Spend
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold mb-6">
              <span className="text-white">Most Agencies Run Campaigns on </span>
              <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">Broken Foundations</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto mb-8 font-body leading-relaxed">
              Weak tracking. Poor attribution. Campaigns flying blind. <strong className="text-white">Sound familiar?</strong> We fix the infrastructure <em>before</em> spending a dollar on ads. That's the AI-first difference.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                { metric: '30-50%', label: 'Lower CAC', desc: 'Through signal optimization' },
                { metric: '2-3x', label: 'Better Leads', desc: 'AI audience modeling' },
                { metric: '40%+', label: 'ROAS Lift', desc: 'Structured testing' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 rounded-2xl bg-void/50 border border-brand/20"
                >
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-brand to-accent-pink bg-clip-text text-transparent mb-1">
                    {stat.metric}
                  </div>
                  <div className="text-white font-semibold mb-1">{stat.label}</div>
                  <div className="text-white/50 text-sm">{stat.desc}</div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/ai-digital-marketing-agency')}
                className="bg-gradient-to-r from-brand to-accent-pink hover:opacity-90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl shadow-brand/30 group"
              >
                See How We're Different
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-6 text-lg font-semibold"
              >
                Book Strategy Call
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries Marquee Section */}
      <section id="industries" className="py-32 px-4 sm:px-6 lg:px-8 bg-void relative overflow-hidden">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand mb-6">
              Industries We Serve
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
              Industry-Specific Marketing
            </h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto font-body">
              We provide specialized <a href="/services/paid-media-advertising" className="text-brand hover:underline">digital advertising</a> and <a href="/services/search-engine-optimization" className="text-brand hover:underline">SEO strategies</a> tailored for <a href="/industries/ecommerce-retail-marketing" className="text-brand hover:underline">ecommerce</a>, <a href="/industries/saas-technology-marketing" className="text-brand hover:underline">SaaS</a>, <a href="/industries/healthcare-wellness-marketing" className="text-brand hover:underline">healthcare</a>, and more.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {industries.map((industry, index) => {
              const IconComponent = iconMap[industry.icon] || Users;
              return (
                <motion.div
                  key={industry.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => navigate(`/industries/${industry.slug}`)}
                  className="cursor-pointer"
                >
                  <Card className="bg-surface/50 backdrop-blur-sm border border-brand/10 hover:border-brand/30 transition-all p-6 text-center group">
                    <div className="w-12 h-12 bg-brand/20 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-brand/30 transition-colors">
                      <IconComponent className="text-brand" size={24} />
                    </div>
                    <h3 className="font-heading font-bold text-white text-sm">{industry.name}</h3>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-surface relative">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand mb-6">
              FAQ
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Common Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqItems.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card 
                  className={`bg-void/50 backdrop-blur-sm border transition-all cursor-pointer ${openFaq === index ? 'border-brand/50' : 'border-brand/10 hover:border-brand/30'}`}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <div className="p-6">
                    <div className="flex justify-between items-center">
                      <h3 className="font-heading font-bold text-white pr-4">{faq.question}</h3>
                      <ChevronDown 
                        className={`text-brand transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                        size={24} 
                      />
                    </div>
                    {openFaq === index && (
                      <motion.p 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="text-white/70 font-body mt-4 leading-relaxed"
                      >
                        {faq.answer}
                      </motion.p>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-4 sm:px-6 lg:px-8 bg-void relative">
        <GlowingOrbs isMobile={isMobile} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand mb-6">
                Free Marketing Consultation
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Get Your Free Digital Marketing Strategy
              </h2>
              <p className="text-xl text-white/70 mb-8 font-body">
                Ready to grow your business? Contact our AI-powered marketing team for a free consultation. We serve businesses across Canada and internationally, helping you increase leads and revenue.
              </p>
              
              <div className="space-y-6">
                <a href="mailto:marketing@ransen.ca" className="flex items-center space-x-4 group hover:opacity-80 transition-opacity">
                  <div className="w-12 h-12 bg-brand/20 rounded-xl flex items-center justify-center group-hover:bg-brand/30 transition-colors">
                    <Mail className="text-brand" size={24} />
                  </div>
                  <span className="text-white/80 font-body group-hover:text-white transition-colors">marketing@ransen.ca</span>
                </a>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-brand/20 rounded-xl flex items-center justify-center">
                    <MapPin className="text-brand" size={24} />
                  </div>
                  <span className="text-white/80 font-body">King St, Toronto, Canada</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="bg-surface/50 backdrop-blur-xl border border-brand/20 p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">Name</label>
                      <Input 
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                        className="bg-void/50 border-brand/20 text-white placeholder:text-white/40 focus:border-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">Email</label>
                      <Input 
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                        className="bg-void/50 border-brand/20 text-white placeholder:text-white/40 focus:border-brand"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Phone</label>
                    <Input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                      className="bg-void/50 border-brand/20 text-white placeholder:text-white/40 focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Website <span className="text-white/40">(Optional)</span></label>
                    <Input 
                      type="url"
                      name="website"
                      value={formData.website || ''}
                      onChange={handleInputChange}
                      placeholder="https://yourwebsite.com"
                      className="bg-void/50 border-brand/20 text-white placeholder:text-white/40 focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Message</label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      placeholder="Tell us about your project..."
                      className="bg-void/50 border-brand/20 text-white placeholder:text-white/40 focus:border-brand resize-none"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-brand-dark to-brand hover:opacity-90 text-white rounded-full py-6 text-lg font-medium disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <ArrowRight className="ml-2" size={20} />}
                  </Button>
                </form>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* RANSEN Acronym Scroll Section */}
      <RansenAcronym />

      {/* CTA Banner */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-brand-dark to-brand relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Scale Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join 150+ companies that have transformed their digital presence with RANSEN.
            </p>
            <Button 
              onClick={() => scrollToSection('contact')} 
              className="bg-white text-brand-dark hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium"
            >
              Start Your Journey Today
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
